import type { ApplicationCreate, AppModelDefinition as AppDefDto } from '@kibsi/ks-application-types'
import { inject, injectable } from 'inversify'
import { makeAutoObservable } from 'mobx'
import { Application as AppDto } from '@kibsi/ks-ui-types'
import T from '../../config/inversify.types'
import type { ApplicationService } from '../../service'
import { AsyncDomainStore, AsyncFromDtoDomainStore } from '../domain'
import { UndoRedoHistory } from '../undo.redo.history'
import { Application } from './application'
import { ApplicationDef } from './application.def'
import { AppHistory, AppHistoryItem } from './history'
import { EditableApplicationDef } from './application.def.editable'
import { PaginatedList, PaginatedListImpl, createDomainPageAction } from '../pagination'
import { commonSearchDtoFactory } from '../search'

export type AppList = PaginatedList<Application>

@injectable()
export class ApplicationsStore {
    private history = new Map<string, AppHistory>()
    private apps: AsyncDomainStore<Application, AppDto>
    private defs: AsyncDomainStore<ApplicationDef, AppDefDto>

    constructor(@inject(T.ApplicationService) private service: ApplicationService) {
        this.apps = new AsyncFromDtoDomainStore<AppDto, Application>(
            (id) => service.get(id),
            (id, data) => new Application(data, service, this.getHistory(id)),
        )

        this.defs = new AsyncFromDtoDomainStore<AppDefDto, ApplicationDef>(
            (id) => service.getModelDefinition(id),
            (id, data) => new EditableApplicationDef(id, data, service, this.getHistory(id)),
        )

        makeAutoObservable<ApplicationsStore, 'service'>(this, {
            service: false,
        })
    }

    async loadList(): Promise<AppList> {
        const { apps, service } = this
        const action = createDomainPageAction(apps, service.list.bind(service), (dto) => dto.id)

        const list = new PaginatedListImpl(action, { pageSize: 100, sort: ['lastUpdated-'] }, commonSearchDtoFactory)

        await list.more()

        return list
    }

    loadApp(id: string): Promise<Application> {
        return this.apps.load(id)
    }

    loadDef(id: string): Promise<ApplicationDef> {
        return this.defs.load(id)
    }

    async createApp(app: ApplicationCreate): Promise<Application> {
        const dto = await this.service.create(app)

        return this.apps.set(dto.id, dto)
    }

    async copyApp(app: Application, name: string): Promise<Application> {
        const description = app.description ?? ''

        const newApp = await this.createApp({ name, description })
        const appDef = await this.service.getModelDefinition(app.id)
        await this.service.setModelDefinition(newApp.id, appDef)

        return newApp
    }

    async deleteApplication(dto: AppDto, cascade?: boolean): Promise<void> {
        await this.service.delete(dto, cascade)
        this.apps.delete(dto.id)
    }

    private getHistory(id: string): AppHistory {
        let history = this.history.get(id)

        if (!history) {
            history = new UndoRedoHistory<AppHistoryItem>()
            this.history.set(id, history)
        }

        return history
    }
}
